import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { frameThrottle } from '@/lib/frameThrottle'

export interface LedgerColumnOption {
  value: string
  label: string
}

export interface LedgerColumn<T> {
  key: string
  label: string
  /** Tailwind width classes for fixed columns (e.g. 'w-24'). Omit for the flexible column. */
  widthClass?: string
  /** Providing a sort value makes the column header sortable. */
  sortValue?: (row: T) => string | number
  /** Sort direction used the first time this column is clicked. */
  defaultSortDir?: 'asc' | 'desc'
  /** Per-column filter input rendered in the filter row. */
  filter?: 'text' | 'select'
  /** Options for a select filter. Derived from row values when omitted. */
  filterOptions?: LedgerColumnOption[]
  /** Value used for filtering (substring match for text, exact match for select). */
  filterValue?: (row: T) => string
  cellClass?: string
  render: (row: T) => ReactNode
}

/** The table's interaction state, reported to the parent in server mode. */
export interface LedgerServerQuery {
  filters: Record<string, string>
  sort: { key: string; dir: 'asc' | 'desc' } | null
  page: number
  pageSize: number
}

export interface LedgerTableServerProps {
  /** Total matching rows on the server (drives counts + page math). */
  totalItems: number
  /**
   * Fired on mount and whenever filters/sort/page/pageSize change. The parent
   * runs the query (debouncing text filters as it sees fit) and passes the
   * resulting page back via `rows`.
   */
  onQueryChange: (query: LedgerServerQuery) => void
  /** Dims the table body while a query is in flight. */
  loading?: boolean
}

export interface LedgerTableProps<T> {
  /**
   * In client mode: the full data set (filtered/sorted/paginated locally).
   * In server mode: just the current page, already filtered and sorted.
   */
  rows: T[]
  columns: LedgerColumn<T>[]
  rowKey: (row: T) => string
  /** e.g. 'callouts' — used in the count strip and empty states */
  itemLabelPlural: string
  emptyLabel?: string
  title?: string
  description?: string
  /** Extra header actions (e.g. a New button), rendered to the right of the built-in toggles. */
  actions?: ReactNode
  /** Extra toggle buttons, rendered right beside "Show completed". */
  toggles?: ReactNode
  /** Rows counted as completed are hidden until "Show completed" is toggled on. */
  isComplete?: (row: T) => boolean
  onRowClick?: (row: T) => void
  /**
   * Extra row classes (e.g. selected-row highlight). When returned, replaces
   * the default zebra background for that row.
   */
  rowHighlight?: (row: T) => string | undefined
  defaultSort?: { key: string; dir: 'asc' | 'desc' }
  /** When false, all rows render at once and the pagination footer is hidden. */
  paginated?: boolean
  initialPageSize?: number
  /**
   * Mirror per-column filters into URL query params (keyed by column key) so
   * filtered views are shareable and survive reloads. On by default.
   */
  syncFiltersToUrl?: boolean
  /** Minimum table width before horizontal scrolling kicks in. */
  minTableWidthClass?: string
  /**
   * Server-driven mode: filtering, sorting and pagination run on the server.
   * The table keeps owning the filter/sort/page state (and URL sync) but
   * applies none of it locally — every change is reported via onQueryChange.
   * Select-filter columns should provide static filterOptions, since options
   * can't be derived from a single page of rows.
   */
  server?: LedgerTableServerProps
  /**
   * Renders one row as a stacked card on small screens (< md). When provided,
   * the wide table is hidden on mobile in favour of a card list with a single
   * search box and a compact sort control.
   */
  mobileCard?: (row: T) => ReactNode
  /**
   * Which column's filter the mobile search box drives in server mode. In
   * client mode the search matches across every text-filter column instead.
   */
  mobileSearchKey?: string
  /**
   * Column keys rendered as compact multi-select filters under the mobile
   * search box. Text-filter columns get their options derived from row values;
   * select columns use their normal options. Multiple picks are stored
   * comma-joined in the column's filter and OR-matched, so this is client-mode
   * only — server adapters see the raw comma-joined string.
   */
  mobileFilterKeys?: string[]
  /**
   * Render the table at its natural height and let the page scroll, instead of
   * the fixed-height internal scroll viewport. Suited to short, embedded tables
   * (e.g. a customer's services) where the viewport's reserved scrollbar gutter
   * would otherwise spill a phantom horizontal scrollbar. Best paired with
   * `paginated={false}`.
   */
  autoHeight?: boolean
}

/** Comma-joined multi-values, OR-matched. Bare commas never appear in values. */
function filterTokens(query: string): string[] {
  return query.split(',').filter((token) => token !== '')
}

function pluralLabel(label: string): string {
  const lower = label.toLowerCase()
  return lower + (lower.endsWith('s') ? 'es' : 's')
}

function readStoredLedgerState(key: string | null): {
  filters?: Record<string, unknown>
  sort?: { key?: unknown; dir?: unknown }
} {
  if (!key || typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object'
      ? (parsed as ReturnType<typeof readStoredLedgerState>)
      : {}
  } catch {
    return {}
  }
}

function FilterMultiSelect({
  label,
  options,
  selected,
  onChange,
  compact = false,
}: {
  label: string
  options: LedgerColumnOption[]
  selected: string[]
  onChange: (values: string[]) => void
  /** Dense styling for the desktop filter row; default is the mobile chip. */
  compact?: boolean
}) {
  const plural = pluralLabel(label)
  const active = selected.length > 0
  const buttonLabel = !active
    ? compact
      ? 'All'
      : `All ${plural}`
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} ${plural}`

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${label}`}
          className={
            compact
              ? [
                  'flex h-7 w-full cursor-pointer items-center justify-between gap-1 rounded border px-1.5 text-[12.5px] font-normal',
                  active
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white text-slate-700',
                ].join(' ')
              : [
                  'flex h-9 min-w-0 flex-1 cursor-pointer items-center justify-between gap-1.5 rounded-lg border px-2.5 text-[13px]',
                  active
                    ? 'border-blue-400 bg-blue-50 font-medium text-blue-800'
                    : 'border-slate-300 bg-white text-slate-700',
                ].join(' ')
          }
        >
          <span className="truncate">{buttonLabel}</span>
          <span aria-hidden className="shrink-0 text-[10px] text-slate-400">
            ▾
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5">
        <button
          type="button"
          onClick={() => onChange([])}
          className={[
            'w-full cursor-pointer rounded px-2 py-2 text-left text-[13px]',
            active ? 'text-slate-700 hover:bg-slate-50' : 'font-medium text-blue-700',
          ].join(' ')}
        >
          All {plural}
        </button>
        <div className="max-h-64 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-2 text-[13px] text-slate-800 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                className="h-4 w-4 shrink-0 accent-blue-600"
              />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function headerCellClass(sorted: boolean): string {
  return [
    'sticky top-0 z-10 h-9 whitespace-nowrap border-b border-slate-200 px-3 text-left text-[13px] font-semibold',
    sorted ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-slate-600',
  ].join(' ')
}
const filterCellClass =
  'sticky top-9 z-10 border-b border-slate-200 bg-white px-2 py-1 shadow-[0_1px_0_#e2e8f0]'
function filterInputClass(active: boolean): string {
  return [
    'h-7 w-full rounded border px-1.5 text-[12.5px] text-slate-900 placeholder:text-slate-300 focus:outline-2 focus:outline-blue-300 focus:-outline-offset-1',
    active ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white',
  ].join(' ')
}

function pageButtonClass(disabled: boolean): string {
  return [
    'inline-flex h-7 w-7 items-center justify-center rounded-md border text-sm leading-none',
    disabled
      ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300'
      : 'cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
  ].join(' ')
}

function isInteractiveTarget(event: MouseEvent): boolean {
  const target = event.target as HTMLElement | null
  return Boolean(target?.closest('a, button, input, select, textarea, [role="menu"]'))
}

export function LedgerTable<T>({
  rows,
  columns,
  rowKey,
  itemLabelPlural,
  emptyLabel,
  title,
  description,
  actions,
  toggles,
  isComplete,
  onRowClick,
  rowHighlight,
  defaultSort,
  paginated = true,
  initialPageSize = 10,
  syncFiltersToUrl = true,
  minTableWidthClass = 'min-w-[1080px]',
  server,
  mobileCard,
  mobileSearchKey,
  mobileFilterKeys,
  autoHeight = false,
}: LedgerTableProps<T>) {
  // Filters and sort survive reloads: the URL wins (shareable links), then
  // the per-path localStorage copy, then the table's defaults.
  const storageKey =
    typeof window !== 'undefined'
      ? `jobcard.ledgerState.${window.location.pathname}`
      : null

  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    () => {
      if (syncFiltersToUrl && typeof window !== 'undefined') {
        const raw = new URLSearchParams(window.location.search).get('sort')
        const [key, dir] = raw?.split(':') ?? []
        if (
          (dir === 'asc' || dir === 'desc') &&
          columns.some((column) => column.key === key && column.sortValue)
        ) {
          return { key, dir }
        }
      }
      const stored = readStoredLedgerState(storageKey).sort
      if (
        stored &&
        (stored.dir === 'asc' || stored.dir === 'desc') &&
        columns.some(
          (column) => column.key === stored.key && column.sortValue,
        )
      ) {
        return { key: stored.key as string, dir: stored.dir }
      }
      return defaultSort ?? null
    },
  )
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    if (typeof window === 'undefined') return {}
    if (syncFiltersToUrl) {
      const params = new URLSearchParams(window.location.search)
      const fromUrl: Record<string, string> = {}
      for (const column of columns) {
        if (!column.filter) continue
        const value = params.get(column.key)
        if (value) fromUrl[column.key] = value
      }
      if (Object.keys(fromUrl).length > 0) return fromUrl
    }
    const stored = readStoredLedgerState(storageKey).filters
    if (!stored) return {}
    const fromStorage: Record<string, string> = {}
    for (const column of columns) {
      const value = stored[column.key]
      if (column.filter && typeof value === 'string' && value) {
        fromStorage[column.key] = value
      }
    }
    return fromStorage
  })
  const [showCompleted, setShowCompleted] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(initialPageSize)
  // Client-mode mobile search: one query matched across all text columns.
  const [mobileSearch, setMobileSearch] = useState('')

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [scrollState, setScrollState] = useState({
    scrollable: false,
    atBottom: true,
  })

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const scrollable = el.scrollHeight > el.clientHeight + 1
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2
    setScrollState((prev) =>
      prev.scrollable === scrollable && prev.atBottom === atBottom
        ? prev
        : { scrollable, atBottom },
    )
  }, [])

  // Row-count and viewport changes both alter scrollability, so observe the
  // container and the table itself rather than depending on render data.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [updateScrollState])

  // Fill the scroll viewport down to the bottom of the window. The class
  // fallback guesses the chrome above at a fixed 310px, which leaves dead
  // space when the real header stack is shorter — measure the actual offsets
  // instead (top of the viewport, plus whatever the card renders below it,
  // e.g. the pagination footer) and let the min-h floor keep short windows
  // usable. Measured on mount and window resize only; mid-session header
  // growth just reverts to the (safe) previous value until the next resize.
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [fillMaxHeight, setFillMaxHeight] = useState<number | null>(null)
  useEffect(() => {
    if (autoHeight) return
    const measure = frameThrottle(() => {
      const el = scrollRef.current
      const card = cardRef.current
      if (!el || !card) return
      const rect = el.getBoundingClientRect()
      const belowViewport = card.getBoundingClientRect().bottom - rect.bottom
      const pageBottomGap = 24
      setFillMaxHeight(
        Math.floor(window.innerHeight - rect.top - belowViewport - pageBottomGap),
      )
    })
    measure()
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('resize', measure)
      measure.cancel()
    }
  }, [autoHeight])

  const pageSizeOptions = useMemo(
    () => [...new Set([initialPageSize, 10, 25, 50])].sort((a, b) => a - b),
    [initialPageSize],
  )

  const anyFilterActive = Object.values(filters).some((value) => value !== '')

  // replaceState (not pushState) so typing in a filter doesn't spam history;
  // history.state is passed through untouched to keep the router's own
  // back/forward tracking intact.
  const writeFiltersToUrl = (next: Record<string, string>) => {
    if (!syncFiltersToUrl || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    for (const column of columns) {
      if (!column.filter) continue
      const value = next[column.key]
      if (value) url.searchParams.set(column.key, value)
      else url.searchParams.delete(column.key)
    }
    window.history.replaceState(window.history.state, '', url)
  }

  const setFilter = (key: string, value: string) => {
    const next = { ...filters, [key]: value }
    setFilters(next)
    writeFiltersToUrl(next)
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({})
    writeFiltersToUrl({})
    setPage(1)
  }

  const writeSortToUrl = (next: { key: string; dir: 'asc' | 'desc' }) => {
    if (!syncFiltersToUrl || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.set('sort', `${next.key}:${next.dir}`)
    window.history.replaceState(window.history.state, '', url)
  }

  const toggleSort = (column: LedgerColumn<T>) => {
    if (!column.sortValue) return
    const next: { key: string; dir: 'asc' | 'desc' } =
      sort?.key === column.key
        ? { key: column.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, dir: column.defaultSortDir ?? 'asc' }
    setSort(next)
    writeSortToUrl(next)
  }

  const setMobileSortKey = (key: string) => {
    const column = columns.find((c) => c.key === key)
    if (!column?.sortValue) return
    const next = { key, dir: column.defaultSortDir ?? ('asc' as const) }
    setSort(next)
    writeSortToUrl(next)
  }

  const flipMobileSortDir = () => {
    if (!sort) return
    const next = {
      key: sort.key,
      dir: sort.dir === 'asc' ? ('desc' as const) : ('asc' as const),
    }
    setSort(next)
    writeSortToUrl(next)
  }

  // Mobile search drives one server filter (mobileSearchKey) in server mode,
  // or the local cross-column search in client mode.
  const mobileSearchValue = server
    ? (mobileSearchKey ? (filters[mobileSearchKey] ?? '') : '')
    : mobileSearch
  const setMobileSearchValue = (value: string) => {
    if (server) {
      if (mobileSearchKey) setFilter(mobileSearchKey, value)
      return
    }
    setMobileSearch(value)
    setPage(1)
  }

  // Persist the view so an operator's setup survives reloads and navigation.
  useEffect(() => {
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ filters, sort }))
    } catch {
      // Storage blocked or full — the view still works, it just won't stick.
    }
  }, [storageKey, filters, sort])

  // Reflect a storage-restored view in the URL once, so it stays shareable.
  useEffect(() => {
    writeFiltersToUrl(filters)
    if (sort) writeSortToUrl(sort)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Server mode: report every interaction-state change (including the initial
  // URL-derived state on mount) so the parent can run the matching query.
  const onQueryChange = server?.onQueryChange
  useEffect(() => {
    onQueryChange?.({ filters, sort, page, pageSize })
  }, [onQueryChange, filters, sort, page, pageSize])

  const filterOptionsByKey = useMemo(() => {
    const result = new Map<string, LedgerColumnOption[]>()
    for (const column of columns) {
      if (
        column.filter !== 'select' &&
        !(column.filter && mobileFilterKeys?.includes(column.key))
      ) {
        continue
      }
      if (column.filterOptions) {
        result.set(column.key, column.filterOptions)
        continue
      }
      const values = new Set<string>()
      for (const row of rows) {
        const value = column.filterValue?.(row) ?? ''
        if (value) values.add(value)
      }
      result.set(
        column.key,
        [...values].sort().map((value) => ({ value, label: value })),
      )
    }
    return result
  }, [columns, rows, mobileFilterKeys])

  const isServerMode = Boolean(server)

  const filtered = useMemo(() => {
    if (isServerMode) return rows
    const searchQuery = mobileSearch.trim().toLowerCase()
    const searchColumns = searchQuery
      ? columns.filter((column) => column.filter === 'text' && column.filterValue)
      : []
    return rows.filter((row) => {
      for (const column of columns) {
        const query = filters[column.key]
        if (!query || !column.filter) continue
        const value = column.filterValue?.(row) ?? ''
        // Multi-select filters store several values comma-joined; a row needs
        // to match any one of them. Single values behave as before.
        const tokens = filterTokens(query)
        const matches =
          column.filter === 'select'
            ? tokens.some((token) => value === token)
            : tokens.some((token) =>
                value.toLowerCase().includes(token.toLowerCase()),
              )
        if (!matches) return false
      }
      if (
        searchColumns.length > 0 &&
        !searchColumns.some((column) =>
          (column.filterValue?.(row) ?? '').toLowerCase().includes(searchQuery),
        )
      ) {
        return false
      }
      return true
    })
  }, [rows, columns, filters, isServerMode, mobileSearch])

  const completedHiddenCount = useMemo(() => {
    if (!isComplete || showCompleted) return 0
    return filtered.filter(isComplete).length
  }, [filtered, isComplete, showCompleted])

  const visible = useMemo(() => {
    const withCompleted =
      isComplete && !showCompleted
        ? filtered.filter((row) => !isComplete(row))
        : filtered

    if (isServerMode || !sort) return withCompleted
    const column = columns.find((c) => c.key === sort.key)
    const sortValue = column?.sortValue
    if (!sortValue) return withCompleted

    const dir = sort.dir === 'asc' ? 1 : -1
    return [...withCompleted].sort((a, b) => {
      const av = sortValue(a)
      const bv = sortValue(b)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
  }, [filtered, isComplete, showCompleted, sort, columns, isServerMode])

  const totalItems = server ? server.totalItems : visible.length
  const pageCount = paginated ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1
  const currentPage = Math.min(page, pageCount)
  const startIndex = paginated ? (currentPage - 1) * pageSize : 0
  const pageRows = isServerMode
    ? visible
    : paginated
      ? visible.slice(startIndex, startIndex + pageSize)
      : visible
  const rangeStart = totalItems === 0 ? 0 : startIndex + 1
  const rangeEnd = paginated
    ? Math.min(startIndex + pageSize, totalItems)
    : totalItems

  const atFirst = currentPage <= 1
  const atLast = currentPage >= pageCount

  const hasAnyFilterRow = columns.some((column) => column.filter)

  return (
    <div className="space-y-3">
      {(title || actions || toggles || isComplete) && (
        <header className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
          <div>
            {title ? (
              <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
            ) : null}
            {description ? (
              <p className="mt-0.5 text-[13px] text-slate-500">{description}</p>
            ) : null}
          </div>
          {/* Buttons wrap as whole units on narrow screens — a label breaking
              mid-word inside a button reads as a rendering bug. */}
          <div className="flex flex-wrap items-center gap-2.5">
            {isComplete ? (
              <button
                type="button"
                onClick={() => {
                  setShowCompleted((prev) => !prev)
                  setPage(1)
                }}
                className={[
                  'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-3 text-[13px] whitespace-nowrap',
                  showCompleted
                    ? 'border-blue-200 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-300 bg-white text-slate-600',
                ].join(' ')}
              >
                <span className="text-sm leading-none">
                  {showCompleted ? '☑' : '☐'}
                </span>
                Show completed
              </button>
            ) : null}
            {toggles}
            {hasAnyFilterRow ? (
              <button
                type="button"
                onClick={clearFilters}
                className={[
                  'inline-flex h-8 cursor-pointer items-center rounded-lg border bg-white px-3 text-[13px] whitespace-nowrap',
                  anyFilterActive
                    ? 'border-blue-200 text-blue-700'
                    : 'border-slate-200 text-slate-400',
                ].join(' ')}
              >
                Clear filters
              </button>
            ) : null}
            {actions}
          </div>
        </header>
      )}

      <div className="text-[12.5px] text-slate-500">
        {isServerMode ? (
          <>
            <strong className="font-semibold text-slate-900">{totalItems}</strong>{' '}
            {itemLabelPlural}
            {anyFilterActive ? ' matching filters' : ''}
          </>
        ) : (
          <>
            Showing <strong className="font-semibold text-slate-900">{totalItems}</strong>{' '}
            of {rows.length} {itemLabelPlural}
            {completedHiddenCount > 0
              ? ` · ${completedHiddenCount} completed hidden`
              : ''}
          </>
        )}
      </div>

      {mobileCard ? (
        <div className="space-y-2 md:hidden">
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={mobileSearchValue}
            onChange={(event) => setMobileSearchValue(event.target.value)}
            placeholder="Search…"
            aria-label={`Search ${itemLabelPlural}`}
            // 16px font stops iOS zooming in on focus.
            className="h-9 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-[16px] text-slate-900 placeholder:text-slate-400 focus:outline-2 focus:outline-blue-300 focus:-outline-offset-1"
          />
          <select
            value={sort?.key ?? ''}
            onChange={(event) => setMobileSortKey(event.target.value)}
            aria-label="Sort by"
            className="h-9 rounded-lg border border-slate-300 bg-white px-1.5 text-[13px] text-slate-700"
          >
            {sort ? null : <option value="">Sort…</option>}
            {columns
              .filter((column) => column.sortValue)
              .map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
          </select>
          <button
            type="button"
            onClick={flipMobileSortDir}
            disabled={!sort}
            aria-label={`Sort ${sort?.dir === 'asc' ? 'ascending' : 'descending'}`}
            className={[
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-[15px]',
              sort
                ? 'border-slate-300 bg-white text-slate-700'
                : 'border-slate-200 bg-slate-50 text-slate-300',
            ].join(' ')}
          >
            {sort?.dir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        {mobileFilterKeys && mobileFilterKeys.length > 0 ? (
          <div className="flex gap-2">
            {mobileFilterKeys.map((key) => {
              const column = columns.find((c) => c.key === key && c.filter)
              if (!column) return null
              return (
                <FilterMultiSelect
                  key={key}
                  label={column.label}
                  options={filterOptionsByKey.get(key) ?? []}
                  selected={filterTokens(filters[key] ?? '')}
                  onChange={(values) => setFilter(key, values.join(','))}
                />
              )
            })}
          </div>
        ) : null}
        </div>
      ) : null}

      <div ref={cardRef} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            style={
              !autoHeight && fillMaxHeight !== null
                ? { maxHeight: fillMaxHeight }
                : undefined
            }
            className={[
              // autoHeight drops the fixed scroll viewport so the page scrolls
              // instead, and clips both axes. Chromium reports a table's
              // scrollWidth ~1 scrollbar wider than its content, so an `auto`
              // overflow would show a phantom horizontal scrollbar over empty
              // space; overflow-hidden suppresses it. Safe here because the
              // table is hidden below md and never needs real horizontal scroll
              // on desktop (md ≥ its min-width).
              autoHeight
                ? 'overflow-hidden transition-opacity duration-150'
                : 'max-h-[calc(100dvh-310px)] min-h-[240px] overflow-auto transition-opacity duration-150',
              server?.loading ? 'opacity-50' : '',
            ].join(' ')}
          >
          {mobileCard ? (
            <div className="md:hidden">
              {pageRows.map((row, index) => (
                <div
                  key={rowKey(row)}
                  onClick={
                    onRowClick
                      ? (event) => {
                          if (isInteractiveTarget(event)) return
                          onRowClick(row)
                        }
                      : undefined
                  }
                  className={[
                    'border-b border-slate-100',
                    rowHighlight?.(row) ??
                      (index % 2 === 1 ? 'bg-slate-50' : 'bg-white'),
                    onRowClick ? 'cursor-pointer active:bg-blue-50/60' : '',
                  ].join(' ')}
                >
                  {mobileCard(row)}
                </div>
              ))}
              {pageRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-[14px] text-slate-400">
                  {(isServerMode ? !anyFilterActive : rows.length === 0)
                    ? (emptyLabel ?? `No ${itemLabelPlural} have been captured yet.`)
                    : `No ${itemLabelPlural} match these filters.`}
                </div>
              ) : null}
            </div>
          ) : null}
          <table
            className={`w-full table-fixed border-collapse text-[14px] ${minTableWidthClass} ${mobileCard ? 'hidden md:table' : ''}`}
          >
            <thead>
              <tr>
                {columns.map((column) => {
                  const isSorted = sort?.key === column.key
                  return (
                    <th
                      key={column.key}
                      onClick={() => toggleSort(column)}
                      className={[
                        headerCellClass(isSorted),
                        column.widthClass ?? '',
                        column.sortValue ? 'cursor-pointer select-none' : '',
                      ].join(' ')}
                    >
                      {column.label}
                      {column.sortValue ? (
                        <span
                          className={`ml-1 ${isSorted ? 'text-blue-600' : 'text-slate-400'}`}
                        >
                          {isSorted ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      ) : null}
                    </th>
                  )
                })}
              </tr>
              {hasAnyFilterRow ? (
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={filterCellClass}>
                      {column.filter === 'text' ? (
                        <input
                          value={filters[column.key] ?? ''}
                          onChange={(event) =>
                            setFilter(column.key, event.target.value)
                          }
                          placeholder="filter"
                          aria-label={`Filter by ${column.label}`}
                          className={filterInputClass(
                            Boolean(filters[column.key]),
                          )}
                        />
                      ) : column.filter === 'select' ? (
                        <FilterMultiSelect
                          compact
                          label={column.label}
                          options={filterOptionsByKey.get(column.key) ?? []}
                          selected={filterTokens(filters[column.key] ?? '')}
                          onChange={(values) =>
                            setFilter(column.key, values.join(','))
                          }
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ) : null}
            </thead>
            <tbody>
              {pageRows.map((row, index) => (
                <tr
                  key={rowKey(row)}
                  onClick={
                    onRowClick
                      ? (event) => {
                          if (isInteractiveTarget(event)) return
                          onRowClick(row)
                        }
                      : undefined
                  }
                  className={[
                    'border-b border-slate-100 align-top',
                    rowHighlight?.(row) ??
                      (index % 2 === 1 ? 'bg-slate-50' : 'bg-white'),
                    onRowClick ? 'cursor-pointer hover:bg-blue-50/60' : '',
                  ].join(' ')}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-[9px] ${column.cellClass ?? ''}`}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-10 text-center text-[14px] text-slate-400"
                  >
                    {(isServerMode ? !anyFilterActive : rows.length === 0)
                      ? (emptyLabel ?? `No ${itemLabelPlural} have been captured yet.`)
                      : `No ${itemLabelPlural} match these filters.`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {/* Overscroll room so the bottom fade never covers the last row. */}
          {scrollState.scrollable ? <div aria-hidden className="h-12" /> : null}
          </div>
          <div
            aria-hidden
            className={[
              'pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/70 to-transparent transition-opacity duration-200',
              scrollState.scrollable && !scrollState.atBottom
                ? 'opacity-100'
                : 'opacity-0',
            ].join(' ')}
          />
          {isServerMode ? (
            // Kept mounted so the delayed fade-in swallows sub-150ms fetches
            // without a flicker.
            <div
              role="status"
              aria-hidden={!server?.loading}
              className={[
                'pointer-events-none absolute inset-0 z-20 flex items-center justify-center transition-opacity',
                server?.loading
                  ? 'opacity-100 delay-150 duration-200'
                  : 'opacity-0 duration-100',
              ].join(' ')}
            >
              <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-blue-500 border-t-transparent" />
              <span className="sr-only">Loading results…</span>
            </div>
          ) : null}
        </div>

        {paginated ? (
        <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-200 bg-white px-4 py-2 text-[12.5px] text-slate-500">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={String(pageSize)}
              onChange={(event) => {
                setPageSize(Number.parseInt(event.target.value, 10))
                setPage(1)
              }}
              aria-label="Rows per page"
              className="h-7 rounded-md border border-slate-300 bg-white px-1.5 text-[12.5px] text-slate-900"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={String(size)}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3.5">
            <span className="tabular-nums">
              {rangeStart}–{rangeEnd} of {totalItems}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                title="First page"
                disabled={atFirst}
                onClick={() => setPage(1)}
                className={pageButtonClass(atFirst)}
              >
                «
              </button>
              <button
                type="button"
                title="Previous page"
                disabled={atFirst}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                className={pageButtonClass(atFirst)}
              >
                ‹
              </button>
              <span className="px-2 tabular-nums">
                Page {currentPage} of {pageCount}
              </span>
              <button
                type="button"
                title="Next page"
                disabled={atLast}
                onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
                className={pageButtonClass(atLast)}
              >
                ›
              </button>
              <button
                type="button"
                title="Last page"
                disabled={atLast}
                onClick={() => setPage(pageCount)}
                className={pageButtonClass(atLast)}
              >
                »
              </button>
            </div>
          </div>
        </footer>
        ) : null}
      </div>
    </div>
  )
}

export type LedgerBadgeTone = 'blue' | 'violet' | 'teal' | 'amber' | 'slate'

const badgeToneClasses: Record<LedgerBadgeTone, string> = {
  blue: 'bg-blue-100 text-blue-700',
  violet: 'bg-violet-100 text-violet-700',
  teal: 'bg-teal-100 text-teal-700',
  amber: 'bg-amber-100 text-amber-700',
  slate: 'bg-slate-100 text-slate-600',
}

export function LedgerBadge({
  tone,
  children,
}: {
  tone: LedgerBadgeTone
  children: ReactNode
}) {
  return (
    <span
      className={`inline-block rounded-[5px] px-1.5 py-0.5 text-[11.5px] font-semibold ${badgeToneClasses[tone]}`}
    >
      {children}
    </span>
  )
}
